import React, { useEffect, useCallback, useState } from 'react';
import { compose } from 'redux';
import { connect, useSelector } from 'react-redux';
import { LocalizedMessage, standardDateNoSecondsOptions } from 'intl/LocalizedComponents';
import _concat from 'lodash/concat';
import _get from 'lodash/get';
import PropTypes from 'prop-types';
// HOCs
import withFormManagement from 'hocs/WithFormManagement';
import withPermissions from 'hocs/WithPermissions';
import withStateReinit from 'hocs/WithStateReinit';

// Core components
import Card from 'components/core/Card';
import DateTimePicker2 from 'components/core/DateTimePicker2/DateTimePicker2';
import TextField from 'components/core/TextField';
import Typography from 'components/core/Typography';
import Modal2 from 'components/modals/Modal2/Modal2';
// Icons
import { ReactComponent as DangerIcon } from 'assets/icons/danger.svg';
// Permissions
import permissions from 'constants/Permissions';
import OnlyWith from 'components/permissions/OnlyWith';
// Models
import { sanitizeDesc } from 'models/accessWay';
// Custom
import { errorShape } from 'constants/PropTypesShapes';
import FormDialogTemplate from 'components/templates/FormDialogTemplate';
import FormHelper from 'components/misc/FormHelper';
import { dateGenerator, dateFormatter } from 'utils/DateUtils';
import { buildFormErrors, cpmOnDemandAccessLicensePlateConstraints } from 'utils/Forms';
// Utils
import RegexUtils from 'utils/RegexUtils';
import { useIntl } from 'react-intl';
import useCheckExistingOnDemandAccess from './hooks/useCheckExistingOnDemandAccess';
import useCarparkFullWarning from './hooks/useCarparkFullWarning';

function OnDemandAddForm({
  fields: { begin, licensePlate },
  errors,
  hasPermission,
  onFieldsChange,
  onRef,
  onValidate,
  onErrorsChange,
  timezone,
}) {
  const { formatMessage: t } = useIntl();
  const [showDuplicatePlateModal, setShowDuplicatePlateModal] = useState(false);

  const token = useSelector((state) => _get(state, 'auth.token', null));
  const carparkId = useSelector((state) => _get(state, 'cpmCarpark.current.data.id', null));

  // Hook to check for existing on-demand access with the same license plate
  const {
    hasActiveOnDemandAccess,
  } = useCheckExistingOnDemandAccess({
    carparkId,
    licensePlate,
    token,
  });

  // Hook to check if carpark full warning
  const {
    showCarparkFullModal,
    checkCarparkFullAndValidate,
    handleContinue,
    handleCancel,
  } = useCarparkFullWarning();

  const handleValidate = useCallback(() => {
    const clientErrors = buildFormErrors(
      { licensePlate },
      cpmOnDemandAccessLicensePlateConstraints,
    );
    onErrorsChange(clientErrors);

    if (!clientErrors) {
      const beginInUTC = dateFormatter.toServerFormat(begin, timezone, { seconds: 0 });

      // Check if there's already active on-demand access for this license plate
      if (hasActiveOnDemandAccess) {
        setShowDuplicatePlateModal(true);
        // Block creation - don't proceed
      } else {
        // Check if carpark is full and handle accordingly
        const validationData = {
          begin: beginInUTC,
          licensePlate,
        };
        checkCarparkFullAndValidate(validationData, onValidate);
      }
    }
  }, [begin, licensePlate, onErrorsChange, onValidate, timezone, hasActiveOnDemandAccess, checkCarparkFullAndValidate]);

  useEffect(() => {
    if (onRef) {
      onRef({ handleValidate });
    }
    // eslint-disable-next-line
  }, [onRef, handleValidate]);

  // Permissions
  const { pSupport } = permissions;

  // Dates
  const beginDate = dateGenerator.dateFromTimezone(begin, timezone);
  const nowDate = dateGenerator.dateFromTimezone(dateGenerator.now(), timezone);

  return (
    <FormDialogTemplate errors={errors}>
      <div className="row">
        <div className="col-12">
          <div className="d-flex">
            <FormHelper
              elements={
                _concat(
                  (
                    hasPermission({ needed: pSupport.onDemandAccessSetBegin, permissionsOrContext: 'carparkPermissions' }) ? [
                      {
                        title: <LocalizedMessage id="field.onDemandBeginDate" />,
                        content: (
                          <LocalizedMessage id="help.onDemand.begin" />
                        ),
                      },
                    ]
                      : []
                  ),
                  [
                    {
                      title: <LocalizedMessage id="field.licensePlate" />,
                      content: (
                        <LocalizedMessage id="help.onDemand.licensePlate" />
                      ),
                    },
                  ],
                )
              }
            />
            <div style={{ flex: 1 }}>
              <Card>
                <div className="row">
                  <OnlyWith
                    permissions={{
                      needed: pSupport.onDemandAccessSetBegin,
                      permissionsOrContext: 'carparkPermissions',
                    }}
                  >
                    <React.Fragment>
                      <div className="col-12 pt-3">
                        <Typography type="subtitle2">
                          <LocalizedMessage id="field.onDemandBeginDate" />
                        </Typography>
                      </div>
                      <div className="col-12 col-lg-9 my-3">
                        <DateTimePicker2
                          name="begin"
                          calendarType="begin"
                          clearable
                          hideIcons
                          errorText={_get(errors, 'begin')}
                          maxDate={nowDate}
                          defaultDate={nowDate}
                          date={beginDate}
                          labelDateId="field.onDemandBeginDate"
                          labelHourId="field.onDemandBeginTime"
                          placeholderId="placeholder.start"
                          calendarLabelId="calendarLabel.start"
                          onChange={(newBegin) => onFieldsChange({ begin: newBegin })}
                        />
                      </div>
                    </React.Fragment>
                  </OnlyWith>
                  <div className="col-12 pt-3">
                    <Typography type="subtitle2">
                      <LocalizedMessage id="field.licensePlate" />
                    </Typography>
                  </div>
                  <div className="col-12 col-lg-9">
                    <TextField
                      label={<LocalizedMessage id="field.licensePlate" />}
                      value={licensePlate}
                      required
                      errorText={_get(errors, 'licensePlate')}
                      onChange={(value) => onFieldsChange({
                        licensePlate: sanitizeDesc(value, 'license_plate'),
                      })}
                    />
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>

      <Modal2
        modalType="alert"
        isOpen={showDuplicatePlateModal}
        onClose={() => setShowDuplicatePlateModal(false)}
        title={t({ id: 'title.existingOnDemandAccessForPlate' })}
        icon={(<DangerIcon />)}
        actions={[
          {
            key: 'close-duplicate-plate',
            label: 'button.close',
            type: 'alert',
            onClick: () => setShowDuplicatePlateModal(false),
          },
        ]}
      >
        <LocalizedMessage id="confirm.createOnDemandAccessDuplicateLicensePlateDescription" />
      </Modal2>

      <Modal2
        modalType="warning"
        isOpen={showCarparkFullModal}
        onClose={handleCancel}
        title={t({ id: 'title.carparkIsFull' })}
        actions={[
          {
            key: 'cancel-carpark-full',
            label: 'button.cancel',
            type: 'secondaryOutline',
            onClick: handleCancel,
          },
          {
            key: 'continue-carpark-full',
            label: 'button.continue',
            type: 'warning',
            onClick: handleContinue,
          },
        ]}
      >
        <LocalizedMessage id="confirm.createOnDemandAccessCarparkFull" />
      </Modal2>
    </FormDialogTemplate>
  );
}

OnDemandAddForm.propTypes = {
  operationError: errorShape, // eslint-disable-line react/no-unused-prop-types
  fields: PropTypes.object.isRequired,
  errors: PropTypes.object,
  hasPermission: PropTypes.func.isRequired,

  onFieldsChange: PropTypes.func.isRequired,
  onRef: PropTypes.func.isRequired,
  onValidate: PropTypes.func.isRequired,
  onErrorsChange: PropTypes.func.isRequired,
  timezone: PropTypes.string.isRequired,
};

OnDemandAddForm.defaultProps = {
  operationError: null,
  errors: null,
};

const mapFieldsToProps = {
  begin: {
    defaultValue: null,
  },
  licensePlate: {
    defaultValue: '',
  },
};

const mapOwnPropsToResources = (ownProps) => ({
  operationError: {
    ...ownProps.operationError,
    messageValuesExtractors: {
      begin: [
        {
          key: 'date',
          pattern: RegexUtils.dateTime,
          setTransform: (value, locOwnProps) => locOwnProps.intl.formatDate(value, standardDateNoSecondsOptions),
        },
      ],
    },
  },
});

const mapStateToProps = (state) => ({
  operationError: state.cpmCarparkOnDemand.add.error,
});

module.exports = compose(
  connect(mapStateToProps, null),
  withFormManagement(mapFieldsToProps, mapOwnPropsToResources, false),
  withPermissions,
  withStateReinit(['addCarparkOnDemand']),
)(OnDemandAddForm);



// WEBPACK FOOTER //
// ./routes/cpm/routes/carparks/assets/onDemand/OnDemandAddForm.js